/**
 * Message Enhancer
 *
 * Enhances raw messages with user information and conversation context
 */

import { EnhancedMessage, Conversation } from "@/types/centralizedChat";

export interface MessageEnhancementContext {
  user: { id: string; full_name?: string | null };
  roles: { admin?: boolean; manufacturer?: boolean } | null;
  conversation?: Conversation | null;
}

/**
 * Enhance a single message with sender information
 */
export function enhanceMessage(
  message: any,
  context: MessageEnhancementContext
): EnhancedMessage {
  if (!message) {
    throw new Error("Message is required for enhancement");
  }
  const { user, roles, conversation } = context;
  const sender = message.users || null;

  // Determine sender role
  let role: "customer" | "manufacturer" | "admin" = "customer";
  if (message.user_id === user.id) {
    role = roles?.admin
      ? "admin"
      : roles?.manufacturer
        ? "manufacturer"
        : "customer";
  } else if (conversation?.participants) {
    const participant = conversation.participants.find(
      (p: any) => p.userId === message.user_id
    );
    if (participant) {
      role = participant.role;
    }
  }

  // Determine display name
  let displayName = "You";
  if (message.user_id !== user.id) {
    if (sender?.full_name) {
      displayName = sender.full_name;
    } else if (conversation) {
      if (role === "customer" && conversation.customerInfo?.fullName) {
        displayName = conversation.customerInfo.fullName;
      } else if (
        role === "manufacturer" &&
        conversation.manufacturerInfo?.contactName
      ) {
        displayName = conversation.manufacturerInfo.contactName;
      } else {
        displayName = `User ${message.user_id?.slice(0, 8)}`;
      }
    } else {
      displayName = `User ${message.user_id?.slice(0, 8)}`;
    }
  }

  return {
    ...message,
    senderInfo: { role, displayName },
    isOwn: message.user_id === user.id,
    referencedTicketId: message.referenced_ticket_id || null,
  };
}
